/**
 * Brand Bible wizard store (zustand) — same layer the content-planner wizard uses.
 * State invariants:
 *  - `score` is ONLY ever set from server responses (mutations return it in-band;
 *    contract §4.3 / DoD #13). Never recomputed client-side — one formula, one owner.
 *  - Entries mirror the server's base-table view (unverified/expired included).
 *  - A score:null on a mutation response means the mutation COMMITTED but scoring
 *    failed — keep the entry, keep the previous score, surface the warning.
 */
import { create } from 'zustand';
import { apiErrorCode, brandBibleV2Service } from '@/services/brandBibleV2Service';
import type {
  BbEntry,
  BbPatchPayload,
  BbTable,
  BibleScore,
  EnrichResponse,
  GatherResponse,
  MutationResponse,
} from '@/features/brand-bible-wizard/types';

type EntriesMap = { [K in BbTable]?: BbEntry<K>[] };

interface WizardState {
  tenantId: string | null;
  step: number; // 1..6
  loading: boolean;
  saving: boolean;
  enriching: boolean;
  entries: EntriesMap;
  score: BibleScore | null;
  /** Points gained since the wizard opened — the "+N pts this step" chip. */
  sessionStartScore: number | null;
  verifyAsYouGo: boolean;
  lastWarning: string | null;
  lastEnrich: EnrichResponse | null;
  /** The web gather request is in flight: the page holds its autosave while this is true (R19). */
  /** True only while a gather is running for the CURRENT tenant (derived from gatherRunningFor by a store
   * subscription): A's run never holds B's saves. */
  gatherRunning: boolean;
  /** The tenant a gather is running for. */
  gatherRunningFor: string | null;
  lastGather: GatherResponse | null;
  /** Appendix A.7: the tenant whose `entries`/`score` are CURRENTLY valid —
   * distinct from `tenantId`, which flips to the REQUESTED tenant the
   * instant init() is called, before its fetch resolves. A diff-sync must
   * never treat the store as loaded for a tenant unless this matches. */
  loadedTenantId: string | null;

  init: (tenantId: string) => Promise<void>;
  /** Appendix A.7 rule 2: resolves true once the store is loaded for
   * `tenantId` — returns immediately if already loaded, awaits (and starts,
   * if none is running) an in-flight init otherwise, bounded so a caller
   * can never hang forever. Resolves false on timeout or init failure —
   * callers must treat false as "do not proceed," never as "proceed anyway." */
  awaitLoaded: (tenantId: string, timeoutMs?: number) => Promise<boolean>;
  setStep: (step: number) => void;
  setVerifyAsYouGo: (on: boolean) => void;
  upsertEntry: <T extends BbTable>(
    table: T,
    payload: BbPatchPayload<T>,
    id: string | undefined,
    /** The tenant the caller captured when its save STARTED. If it is not the store's current tenant the call
     * rejects and sends nothing: the store is global and flips on init(), so a stale caller must not write to the
     * new client. Required by the type so tsc lists every caller. */
    tenantId: string,
  ) => Promise<BbEntry<T> | null>;
  verifyEntry: (table: BbTable, id: string, approveForPublic: boolean | undefined, tenantId: string) => Promise<void>;
  unverifyEntry: (table: BbTable, id: string, tenantId: string) => Promise<void>;
  discardEntry: (table: BbTable, id: string, tenantId: string) => Promise<void>;
  runEnrichment: () => Promise<void>;
  /** Page-facing only (never in the WebMCP slice). Always refetches the bible, even after a failure. */
  /** The ONE runner of a long draft job (web gather, questionnaire import/upload). The tenant is required. */
  runDraftJob: <T>(
    tenantId: string | null | undefined,
    kind: 'gather' | 'questionnaire',
    job: () => Promise<T>,
  ) => Promise<T | undefined>;
  runGather: (tenantId: string) => Promise<void>;
  /** Apply a pending identity value, optionally edited; refetches. 409 stale refetches then rethrows. */
  acceptPendingField: (field: string, valueAsSeen: string, value: string | undefined, tenantId: string) => Promise<void>;
  rejectPendingField: (field: string, valueAsSeen: string, tenantId: string) => Promise<void>;
}

// Appendix A.7 rule 1: module-scoped (not store state — this is bookkeeping
// for the fetch lifecycle, not something a component renders from) epoch
// counter + in-flight-init tracker. A stale init's resolution must never
// apply after a newer one has started; the newer one may even have already
// resolved and swapped the store by the time the stale one comes back.
let initEpoch = 0;
let inFlightInit: { tenantId: string; promise: Promise<void> } | null = null;

// Appendix A.8-PERM criterion (a): the write seam. At most ONE in-flight
// write per (tenant, table, entryId) — creates AND patches — lives here,
// module-scoped for the same reason initEpoch is: it's write-lifecycle
// bookkeeping, not render state. Every caller of upsertEntry/discardEntry
// inherits this automatically (§A blur saves, pushDraftToV2, the wizard
// steps, B/C/F/G/H/I panels, file-drop confirm, anything written today or in
// the future) — this is what makes it a seam fix and not a §A-local one
// (A.6's create-only scoping mistake, not repeated).
type WriteKey = string;

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (v: T) => void;
  reject: (e: unknown) => void;
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: (v: T) => void;
  let reject!: (e: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

interface PendingWrite {
  /** What's currently on the wire for this key. */
  issued: Record<string, unknown>;
  /** Coalesced (shallow-merged, latest-key-wins) merge of payloads that
   * arrived while `issued` was in flight — flushed once `issued` settles,
   * REGARDLESS of whether `issued` itself succeeded, reconciled, or
   * definitely failed (Panel 2 P0-1 finding on 172928cce: the pre-fix flush
   * lived only on the success/reconciled-success path, after the try/catch —
   * every throw path skipped it, silently dropping a coalesced edit whose
   * own write was never even attempted). */
  queued: Record<string, unknown> | null;
  /** The in-flight write's own promise, tied to `issued`'s own outcome
   * ONLY. */
  promise: Promise<unknown>;
  /** Created the moment a second caller coalesces onto `queued` — settles
   * with THAT flush's own outcome, never with `issued`'s (Panel 2 finding:
   * 172928cce handed every coalesced caller the ORIGINAL write's promise
   * object, so a caller whose fields were never attempted could see a
   * misleading rejection naming someone else's edit, or vice versa). */
  queuedDeferred: Deferred<unknown> | null;
}

const pendingWrites = new Map<WriteKey, PendingWrite>();

/** A write that names a tenant the store is no longer on: refused before any request. */
function staleTenantError(_caller: string, _current: string): Error {
  // Plain wording on purpose: callers toast e.message, and two client domains must never share a toast.
  return new Error('The workspace changed. Nothing was saved.');
}

/** List-type tables where each no-id upsertEntry call is a DISTINCT new row
 * (locations, services, credentials, ...) — as opposed to the three
 * singleton tables (one row per tenant), where every no-id call before the
 * row exists is legitimately the SAME in-progress row and must coalesce.
 * Panel 2 P0 (ALSO FIX): a shared `table:CREATE` key across list-type
 * creates let two unrelated new rows merge into one (row 2's data ends up
 * PATCHed onto row 1's id via the flush). Singletons keep the shared key;
 * list tables get a fresh key per creation attempt below. */
function isSingletonTable(table: BbTable): boolean {
  return table === 'identity' || table === 'voice_profile' || table === 'visual_identity';
}

let createAttemptCounter = 0;

// Panel 2 (ALSO FIX): `saving` must be DERIVED from how many writes are
// actually in flight, not toggled true/false by each call independently —
// with concurrent writes to different keys now routine (the whole point of
// this seam), the old "last one to settle wins" boolean could flip `saving`
// to false while a sibling write was still running. Covers creates,
// patches, AND deletes (discardEntry increments/decrements it too).
let inFlightOps = 0;

// Appendix A.8-PERM criterion (c): duplicate-value suppression bookkeeping —
// the last value this seam knows to be correct for a given field, updated
// optimistically the moment a write is ISSUED (not just on ack) and
// authoritatively on ack/reconcile. This is what silently absorbs
// pushDraftToV2's stale-diff re-emission (Appendix A.8's named mechanism,
// BrandBiblePage.tsx:1278/1588-1589 as of 1cf1eb643) with ZERO page changes:
// the page still calls upsertEntry a second time with the "same" edit, but
// the seam recognizes the value hasn't changed and drops it before it ever
// reaches the wire.
const lastKnownFieldValues = new Map<WriteKey, Record<string, unknown>>();

/** Panel 2 P0-3: the key MUST be tenant-scoped. 172928cce keyed purely on
 * (table, id) — a memory-hygiene and cross-tenant-suppression risk (nothing
 * ever cleared `lastKnownFieldValues` on a tenant switch, so a stale value
 * from tenant A's session could survive into tenant B's and wrongly suppress
 * a legitimate edit there). */
function writeKey(tenantId: string, table: BbTable, id: string | undefined): WriteKey {
  return id ? `${tenantId}:${table}:${id}` : `${tenantId}:${table}:CREATE`;
}

/** For a NEW (no-id) write on a list-type table, every attempt gets its own
 * key so two concurrent new-row creations never coalesce into one row. */
function createKeyFor(tenantId: string, table: BbTable): WriteKey {
  if (isSingletonTable(table)) return writeKey(tenantId, table, undefined);
  return `${tenantId}:${table}:create-${++createAttemptCounter}`;
}

/** Structural equality good enough for jsonb-shaped payload values (scalars,
 * arrays, flat objects like socials/owner) — not a general deep-equal, but
 * everything this seam's payloads are built from. */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => valuesEqual(v, b[i]));
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const ak = Object.keys(a as object);
    const bk = Object.keys(b as object);
    if (ak.length !== bk.length) return false;
    return ak.every((k) => valuesEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

/** Drop fields from `payload` whose value already matches `known` — the
 * suppression step itself, shared by the initial-issue path and the
 * queued-flush path (both must re-diff, never blind-send). */
function diffAgainstKnown(
  payload: Record<string, unknown>,
  known: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (!(k in known) || !valuesEqual(known[k], v)) out[k] = v;
  }
  return out;
}

/** Appendix A.8-AMEND rule 6 / A.8-PERM criterion (b), widened per Panel 2
 * P0-2: the property that routes to reconcile is "did we learn the server's
 * decision?", not narrowly "was this a client-side timeout." A client
 * timeout (`ECONNABORTED`/`ETIMEDOUT`) is the clearest case, but a gateway
 * gave up too (502/503/504), a proxy declared its own timeout (408), or the
 * request never definitively reached/left the server (`ERR_NETWORK`,
 * `ERR_CANCELED`) are ALL the same kind of unknown — the write may or may
 * not have landed. Only a real application-level response (validation 422,
 * server error 500, etc.) is a genuine, definite verdict. The message-regex
 * check stays as a last-resort fallback for shapes that don't carry a code. */
function isOutcomeUnknown(err: unknown): boolean {
  const e = err as { code?: string; message?: string; response?: { status?: number } } | null | undefined;
  if (!e) return false;
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT') return true;
  if (e.code === 'ERR_NETWORK' || e.code === 'ERR_CANCELED') return true;
  const status = e.response?.status;
  if (status !== undefined && (status === 408 || status === 502 || status === 503 || status === 504)) {
    return true;
  }
  return typeof e.message === 'string' && /timeout of \d+ms exceeded/i.test(e.message);
}

function isNotFound(err: unknown): boolean {
  const e = err as { response?: { status?: number } } | null | undefined;
  return e?.response?.status === 404;
}

/** Best-effort match for a CREATE (no id yet) that timed out — the store
 * has no natural-key concept generically, so this tries the identifying
 * shapes that exist in this table family: location_key, name (case-
 * insensitive), else falls back to "the one live row for this scope" for
 * singleton tables (identity/voice_profile/visual_identity have no natural
 * key in their payload at all).
 *
 * Panel 2 flagged the `name` branch as a "fuzzy" match — it isn't partial/
 * Levenshtein, but it IS matching by a field that isn't always the table's
 * real natural key, and a shared name across two rows would match the wrong
 * one. Left as-is deliberately (not deleted): the correct fix is a
 * backend-issued idempotency id for creates, which Panel 2 is routing as a
 * separate ticket. Until then, a wrong match here is bounded by
 * reconcileAfterTimeout's own mismatch check — a false match just means the
 * attempted fields get compared against the WRONG row's values, which
 * surfaces as "did not persist" (a loud failure), not silent corruption of
 * that row — this function only reads for comparison, never writes. */
function findByBestEffortKey(
  fresh: BbEntry[],
  attempted: Record<string, unknown>,
): BbEntry | undefined {
  if (typeof attempted.location_key === 'string') {
    return fresh.find(
      (e) => (e as unknown as { location_key?: string }).location_key === attempted.location_key,
    );
  }
  if (typeof attempted.name === 'string') {
    const key = attempted.name.trim().toLowerCase();
    return fresh.find(
      (e) => ((e as unknown as { name?: string }).name ?? '').trim().toLowerCase() === key,
    );
  }
  const scope = (attempted.location_scope as string | undefined) ?? 'all';
  const candidates = fresh.filter((e) => e.location_scope === scope);
  return candidates.length === 1 ? candidates[0] : undefined;
}

/** Test-only: clears just the write seam's module-scoped bookkeeping
 * (pendingWrites / lastKnownFieldValues / the create-attempt counter),
 * without touching init's epoch/in-flight tracking. Exported separately per
 * Panel 2's review so seam-focused tests don't need to reach for the
 * init-flavored name. */
export function __resetWriteState(): void {
  pendingWrites.clear();
  lastKnownFieldValues.clear();
  createAttemptCounter = 0;
}

/** Test-only: this bookkeeping lives outside zustand's state on purpose (see
 * above), so `useBrandBibleWizardStore.setState(...)` in a test's beforeEach
 * cannot reset it — a stale `inFlightInit` from a previous test case (e.g.
 * one that intentionally left a promise unresolved to test a timeout) would
 * otherwise leak into the next test and get awaited there. Also clears the
 * A.8-PERM write seam's bookkeeping for the same reason. */
export function __resetInitBookkeepingForTests(): void {
  initEpoch += 1;
  inFlightInit = null;
  __resetWriteState();
}

function applyMutation<T extends BbTable>(
  state: Pick<WizardState, 'entries' | 'score'>,
  table: T,
  res: MutationResponse<T>,
): Partial<WizardState> {
  const list = (state.entries[table] ?? []) as BbEntry<T>[];
  const idx = list.findIndex((e) => e.id === res.entry.id);
  const next =
    res.entry.deleted_at !== null
      ? list.filter((e) => e.id !== res.entry.id)
      : idx >= 0
        ? list.map((e) => (e.id === res.entry.id ? res.entry : e))
        : [...list, res.entry];
  return {
    entries: { ...state.entries, [table]: next },
    // score:null => scoring failed server-side; keep previous score (never fake it).
    score: res.score ?? state.score,
    lastWarning: res.warning ?? null,
  };
}

export const useBrandBibleWizardStore = create<WizardState>((set, get) => {
  // ---------------------------------------------------------------------
  // Appendix A.8-PERM: the write seam. Closures over set/get so they can
  // live inside the store creator; module-scoped bookkeeping (pendingWrites
  // / lastKnownFieldValues) lives above so it's shared across store
  // instances the same way initEpoch/inFlightInit already are.
  // ---------------------------------------------------------------------

  /** Reconcile-on-timeout (A.8-AMEND rule 6 / A.8-PERM criterion b): never
   * report a client timeout as failure without checking server truth first.
   * Re-diffs the attempted fields against the fresh row — fields that now
   * match are a silent success; fields that still don't are a REAL failure
   * (never softened — Carl's rejected list explicitly forbids toast
   * softening). Reused by both upsertEntry (PATCH/CREATE) and discardEntry
   * (DELETE) timeout paths. */
  const reconcileAfterTimeout = async (
    table: BbTable,
    id: string | undefined,
    attempted: Record<string, unknown>,
    tenantId: string,
    key: WriteKey,
  ): Promise<BbEntry<BbTable> | null> => {
    // Deliberately NOT awaitLoaded(): that's a "wait for whatever load is
    // in flight, or skip if already loaded" gate (Appendix A.7's contract
    // for diff-syncs) — reconcile needs the OPPOSITE: a forced fresh fetch
    // every time, regardless of loadedTenantId already matching, since the
    // whole point is to learn whether THIS write changed server state.
    // init() itself is same-tenant-safe (A.7: never wipes entries
    // synchronously for a same-tenant refetch), so this is safe to call
    // unconditionally.
    let loaded = true;
    try {
      // Never re-init a tenant the store has moved off: that would re-point the whole page at it.
      if (get().tenantId !== tenantId) throw new Error('tenant changed');
      await get().init(tenantId);
    } catch {
      loaded = false;
    }
    if (!loaded) {
      // Reconcile itself failed — genuinely unknown, not success. Never
      // claim success we can't verify; don't poison "known" bookkeeping
      // with values that were never confirmed.
      const known = lastKnownFieldValues.get(key);
      if (known) for (const k of Object.keys(attempted)) delete known[k];
      throw new Error(`${String(table)} write timed out and could not be reconciled`);
    }
    const fresh = get().entries[table] ?? [];
    const freshEntry = id ? fresh.find((e) => e.id === id) : findByBestEffortKey(fresh, attempted);

    if (!freshEntry) {
      const known = lastKnownFieldValues.get(key);
      if (known) for (const k of Object.keys(attempted)) delete known[k];
      throw new Error(`${String(table)} row not found after reconcile`);
    }

    const stillMismatched: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(attempted)) {
      if (!valuesEqual((freshEntry as unknown as Record<string, unknown>)[k], v)) {
        stillMismatched[k] = v;
      }
    }

    // Record server truth as known for every attempted field regardless of
    // outcome — fields that matched stay suppressed; fields that didn't
    // must NOT be suppressed on a future retry of the same value (a retry
    // with the SAME desired value after a genuine failure must go out).
    const known = lastKnownFieldValues.get(key) ?? {};
    for (const k of Object.keys(attempted)) {
      known[k] = (freshEntry as unknown as Record<string, unknown>)[k];
    }
    lastKnownFieldValues.set(key, known);

    if (Object.keys(stillMismatched).length > 0) {
      const err = new Error(
        `${String(table)} write partially failed: ${Object.keys(stillMismatched).join(', ')} did not persist`,
      );
      (err as Error & { unpersistedFields?: Record<string, unknown> }).unpersistedFields = stillMismatched;
      throw err;
    }
    return freshEntry;
  };

  /** Issues (or, on timeout, reconciles) ONE write, then flushes any
   * payload that got coalesced onto it while it was in flight. This is the
   * only place that ever calls patchEntry/createEntry — every caller goes
   * through the pendingWrites gate in upsertEntry below first. */
  const runWrite = async (
    table: BbTable,
    id: string | undefined,
    payload: Record<string, unknown>,
    key: WriteKey,
  ): Promise<BbEntry<BbTable> | null> => {
    const { tenantId } = get();
    // A queued flush that outlived a tenant switch must not go to the new tenant: the key carries the tenant it was made for.
    if (!tenantId || !key.startsWith(`${tenantId}:`)) {
      pendingWrites.delete(key);
      return null;
    }
    inFlightOps++;
    set({ saving: true });
    let result: BbEntry<BbTable> | null = null;
    // Panel 2 P0-1: a definite failure (or a reconcile that itself throws)
    // used to `throw` straight out of the catch block, which skipped the
    // flush-the-queue step entirely below — silently dropping any edit that
    // coalesced onto this write while it was in flight. Captured here
    // instead so the flush ALWAYS runs, on every exit path, before this
    // function ever rejects.
    let definiteFailure: unknown = null;
    try {
      const res = id
        ? await brandBibleV2Service.patchEntry(tenantId, table, id, payload as never)
        : await brandBibleV2Service.createEntry(tenantId, table, payload as never);
      // Apply only while the store is still on the tenant this write was for (a late result of the previous
      // tenant must not put its row into the new tenant's entries).
      if (get().tenantId === tenantId) set((s) => applyMutation(s, table, res));
      result = res.entry;
      const known = lastKnownFieldValues.get(key) ?? {};
      for (const k of Object.keys(payload)) known[k] = (res.entry as unknown as Record<string, unknown>)[k];
      lastKnownFieldValues.set(key, known);
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        try {
          result = await reconcileAfterTimeout(table, id, payload, tenantId, key);
        } catch (reconcileErr) {
          definiteFailure = reconcileErr;
        }
      } else {
        // A real, unambiguous failure — surface loudly, never softened.
        definiteFailure = err;
      }
      if (definiteFailure) {
        const known = lastKnownFieldValues.get(key);
        if (known) for (const k of Object.keys(payload)) delete known[k];
      }
    } finally {
      inFlightOps = Math.max(0, inFlightOps - 1);
      set({ saving: inFlightOps > 0 });
    }

    // A create resolved to a real id (via the success path OR a reconciled
    // timeout) — re-key bookkeeping so a subsequent write for this row (and
    // the flush right below) targets tenant:table:id, not the create key.
    // Panel 2 (ALSO FIX): 172928cce only did this migration on the success
    // path, orphaning it under the dead create key when a CREATE's timeout
    // reconciled successfully.
    if (!id && result?.id) {
      const newKey = writeKey(tenantId, table, result.id);
      if (key !== newKey) {
        const vals = lastKnownFieldValues.get(key);
        if (vals) {
          lastKnownFieldValues.set(newKey, vals);
          lastKnownFieldValues.delete(key);
        }
      }
    }

    // Flush any coalesced queue — re-diff at fire time (A.8-AMEND rule 7):
    // never blind-resend a captured payload, and a stale value that was
    // superseded by a newer edit must not go out. This now runs
    // UNCONDITIONALLY — success, reconciled, AND definite-failure paths all
    // reach here (Panel 2 P0-1 fix).
    const pending = pendingWrites.get(key);
    const queued = pending?.queued;
    const queuedDeferred = pending?.queuedDeferred;
    pendingWrites.delete(key);

    if (queued && Object.keys(queued).length > 0) {
      const flushId = id ?? result?.id;
      const knownForFlush = lastKnownFieldValues.get(writeKey(tenantId, table, flushId)) ?? {};
      const toFlush = diffAgainstKnown(queued, knownForFlush);
      if (Object.keys(toFlush).length > 0) {
        const flushKey = writeKey(tenantId, table, flushId);
        const flushPromise = runWrite(table, flushId, toFlush, flushKey);
        pendingWrites.set(flushKey, { issued: toFlush, queued: null, promise: flushPromise, queuedDeferred: null });
        // The caller(s) who coalesced onto `queued` get THIS flush's own
        // outcome (Panel 2 finding) — never `issued`'s, which may have
        // failed for reasons entirely unrelated to the queued fields.
        flushPromise.then((v) => queuedDeferred?.resolve(v)).catch((e) => queuedDeferred?.reject(e));
      } else {
        // Fully suppressed by fresher known values — the queued caller's
        // fields are already correct server-side; resolve with current
        // truth, not with `issued`'s failure.
        const current = flushId ? (get().entries[table] ?? []).find((e) => e.id === flushId) ?? null : null;
        queuedDeferred?.resolve(current);
      }
    } else if (queuedDeferred) {
      // Defensive: a deferred exists with nothing queued (shouldn't happen
      // given upsertEntry always sets queued alongside creating the
      // deferred, but never leave a promise unsettled).
      const flushId = id ?? result?.id;
      const current = flushId ? (get().entries[table] ?? []).find((e) => e.id === flushId) ?? null : null;
      queuedDeferred.resolve(current);
    }

    if (definiteFailure) throw definiteFailure;
    return result;
  };

  return {
  tenantId: null,
  step: 1,
  loading: false,
  saving: false,
  enriching: false,
  entries: {},
  score: null,
  sessionStartScore: null,
  verifyAsYouGo: true,
  lastWarning: null,
  lastEnrich: null,
  gatherRunning: false,
  gatherRunningFor: null,
  lastGather: null,
  loadedTenantId: null,

  // Appendix A.7: the cascade's root cause — this used to wipe `entries`
  // SYNCHRONOUSLY before awaiting getBible, on EVERY init call including a
  // same-tenant re-init (e.g. the identity-save timeout's reconcile path).
  // That opened a window where the store looked like "zero entries for this
  // tenant" to anything reading it mid-fetch — a diff-sync running in that
  // window saw zero locations and mass-created every one that already
  // existed. Fix: wipe synchronously ONLY on tenant change (cross-tenant
  // display must never happen); a same-tenant re-init keeps serving the
  // current entries/score until the fresh fetch resolves, then swaps
  // atomically. Concurrent inits are epoch-guarded — a stale resolution
  // (an older fetch settling after a newer one started, or after a tenant
  // switch) is discarded rather than applied.
  init: (tenantId) => {
    const myEpoch = ++initEpoch;
    const isTenantChange = get().tenantId !== tenantId;
    if (isTenantChange) {
      // Panel 2 P0-3: the write seam's module-scoped maps are keyed by
      // tenant now, so this isn't strictly required for correctness, but a
      // tenant switch means every in-progress write bookkeeping for the
      // PREVIOUS tenant is dead weight — nothing viewing the new tenant will
      // ever address it again. Clear for hygiene (unbounded map growth
      // across a long session otherwise).
      pendingWrites.clear();
      lastKnownFieldValues.clear();
    }
    set({
      tenantId,
      loading: true,
      ...(isTenantChange
        ? { entries: {}, score: null, sessionStartScore: null, loadedTenantId: null }
        : {}),
    });
    const promise = brandBibleV2Service
      .getBible(tenantId)
      .then((bible) => {
        if (myEpoch !== initEpoch) return; // superseded by a newer init — discard
        const tables = bible.tables ?? {};
        // Panel 2 P0-3: rebuild `lastKnownFieldValues` from this fresh
        // fetch for every table/row — the bible envelope is comprehensive
        // (init() replaces `entries` wholesale, not a partial patch), so
        // this is authoritative. Without this, an EXTERNAL change (another
        // session, enrichment, an admin edit) that this init() observes
        // never updates the seam's idea of "known" — a user setting a field
        // back to a value it held BEFORE the external change would be
        // silently suppressed as a no-op instead of firing a real PATCH.
        for (const [tableName, rows] of Object.entries(tables)) {
          for (const row of rows ?? []) {
            const rowKey = writeKey(tenantId, tableName as BbTable, (row as { id: string }).id);
            lastKnownFieldValues.set(rowKey, { ...(row as unknown as Record<string, unknown>) });
          }
        }
        set({
          loading: false,
          entries: tables,
          score: bible.score ?? null,
          sessionStartScore: bible.score?.score ?? null,
          loadedTenantId: tenantId,
        });
      })
      .catch((err: unknown) => {
        if (myEpoch === initEpoch) set({ loading: false });
        throw err;
      })
      .finally(() => {
        if (inFlightInit?.promise === promise) inFlightInit = null;
      });
    inFlightInit = { tenantId, promise };
    return promise;
  },

  awaitLoaded: async (tenantId, timeoutMs = 30000) => {
    // A stale closure asking about a tenant the store has moved off must never re-init it (that would
    // re-point the whole page at the previous client).
    if (get().tenantId !== null && get().tenantId !== tenantId) return false;
    if (get().loadedTenantId === tenantId) return true;
    const promise =
      inFlightInit?.tenantId === tenantId ? inFlightInit.promise : get().init(tenantId);
    const TIMED_OUT = Symbol('timed-out');
    const result = await Promise.race([
      promise.then(() => true as const).catch(() => false as const),
      new Promise<typeof TIMED_OUT>((resolve) => setTimeout(() => resolve(TIMED_OUT), timeoutMs)),
    ]);
    if (result === TIMED_OUT) return false;
    return get().loadedTenantId === tenantId;
  },

  setStep: (step) => set({ step: Math.min(6, Math.max(1, step)) }),
  setVerifyAsYouGo: (on) => set({ verifyAsYouGo: on }),

  // Appendix A.8-PERM criteria (a)+(c): the single-flight + duplicate-value-
  // suppression gate. Every caller — §A blur saves, pushDraftToV2's stale
  // re-diff, the wizard, any future field — goes through here; runWrite
  // above is the only thing that ever touches the network.
  upsertEntry: (table, payload, id, callerTenant) => {
    const { tenantId } = get();
    if (!tenantId) return Promise.resolve(null);
    if (!callerTenant || callerTenant !== tenantId) return Promise.reject(staleTenantError(callerTenant, tenantId));

    // Panel 2 (ALSO FIX): a brand-new row on a list-type table gets its own
    // key per attempt, so two concurrent "add new X" clicks never coalesce
    // into one row. Singletons (identity/voice_profile/visual_identity)
    // keep the shared create key — multiple field edits before that row
    // exists ARE the same in-progress row and must coalesce (A.6).
    const key = id ? writeKey(tenantId, table, id) : createKeyFor(tenantId, table);
    const known = lastKnownFieldValues.get(key) ?? {};
    const filtered = diffAgainstKnown(payload as Record<string, unknown>, known);

    const pending = pendingWrites.get(key);

    if (Object.keys(filtered).length === 0) {
      // Every field in this write already matches the last-known
      // (acknowledged or in-flight) value — a redundant re-emission
      // (pushDraftToV2's stale diff re-firing the same edit while the
      // original write is still in flight). Drop it before it reaches the
      // wire; hand back whatever's already in flight, or the current store
      // value if nothing is.
      if (pending) {
        return (pending.queuedDeferred?.promise ?? pending.promise) as Promise<BbEntry<typeof table> | null>;
      }
      const current = id ? (get().entries[table] ?? []).find((e) => e.id === id) : undefined;
      return Promise.resolve((current ?? null) as BbEntry<typeof table> | null);
    }

    // Optimistically record the filtered fields as known NOW (not just on
    // ack) — a THIRD redundant call arriving before this one even resolves
    // must also be suppressed, matching A.8-AMEND's "in-flight counts as
    // already current" framing.
    Object.assign(known, filtered);
    lastKnownFieldValues.set(key, known);

    if (pending) {
      // Coalesce: shallow-merge, latest-key-wins. Panel 2 finding: this
      // caller must get a promise tied to the eventual FLUSH's own outcome,
      // never `issued`'s (the original in-flight write's) — those are two
      // different network operations with two different verdicts.
      pending.queued = { ...pending.queued, ...filtered };
      if (!pending.queuedDeferred) pending.queuedDeferred = createDeferred();
      return pending.queuedDeferred.promise as Promise<BbEntry<typeof table> | null>;
    }

    const promise = runWrite(table, id, filtered, key);
    pendingWrites.set(key, { issued: filtered, queued: null, promise, queuedDeferred: null });
    return promise as Promise<BbEntry<typeof table> | null>;
  },

  verifyEntry: async (table, id, approveForPublic = false, callerTenant) => {
    const { tenantId } = get();
    if (!tenantId) return;
    if (!callerTenant || callerTenant !== tenantId) throw staleTenantError(callerTenant, tenantId);
    const res = await brandBibleV2Service.verifyEntry(tenantId, table, id, approveForPublic);
    if (get().tenantId === tenantId) set((s) => applyMutation(s, table, res));
  },

  unverifyEntry: async (table, id, callerTenant) => {
    const { tenantId } = get();
    if (!tenantId) return;
    if (!callerTenant || callerTenant !== tenantId) throw staleTenantError(callerTenant, tenantId);
    const res = await brandBibleV2Service.unverifyEntry(tenantId, table, id);
    if (get().tenantId === tenantId) set((s) => applyMutation(s, table, res));
  },

  // Appendix A.8-AMEND rule 7 / A.8-PERM criterion (e): idempotent,
  // non-resurrecting retries. A delete that times out but actually
  // succeeded, or a retry that hits 404 because an earlier attempt already
  // landed, must resolve as success — never a false error toast.
  discardEntry: async (table, id, callerTenant) => {
    const { tenantId } = get();
    if (!tenantId) return;
    if (!callerTenant || callerTenant !== tenantId) throw staleTenantError(callerTenant, tenantId);
    const key = writeKey(tenantId, table, id);
    // Single-flight awareness: ride out any in-flight create/patch for this
    // exact row first so the delete doesn't race it.
    const existingPending = pendingWrites.get(key);
    if (existingPending) {
      await (existingPending.queuedDeferred?.promise ?? existingPending.promise).catch(() => undefined);
    }
    inFlightOps++;
    set({ saving: true });
    try {
      const res = await brandBibleV2Service.deleteEntry(tenantId, table, id);
      if (get().tenantId === tenantId) set((s) => applyMutation(s, table, res));
      lastKnownFieldValues.delete(key);
    } catch (err) {
      if (isNotFound(err)) {
        // Already gone — a prior attempt (or another tab) succeeded first.
        // Render as success, not an error.
        set((s) => ({ entries: { ...s.entries, [table]: (s.entries[table] ?? []).filter((e) => e.id !== id) } }));
        lastKnownFieldValues.delete(key);
        return;
      }
      if (isOutcomeUnknown(err)) {
        // Forced fresh fetch, not awaitLoaded() — same reasoning as
        // reconcileAfterTimeout above: loadedTenantId already matching
        // must not short-circuit a reconcile that needs to observe
        // whatever THIS delete changed server-side.
        let loaded = true;
        try {
          if (get().tenantId !== tenantId) throw new Error('tenant changed');
          await get().init(tenantId);
        } catch {
          loaded = false;
        }
        if (loaded) {
          const fresh = get().entries[table] ?? [];
          const stillThere = fresh.some((e) => e.id === id);
          if (!stillThere) {
            // Gone after reconcile — the delete landed despite the client
            // timeout. Success, no throw.
            lastKnownFieldValues.delete(key);
            return;
          }
        }
      }
      throw err;
    } finally {
      inFlightOps = Math.max(0, inFlightOps - 1);
      set({ saving: inFlightOps > 0 });
    }
  },

  runEnrichment: async () => {
    const { tenantId } = get();
    if (!tenantId) return;
    set({ enriching: true });
    try {
      const enrich = await brandBibleV2Service.enrich(tenantId);
      // Enrichment writes rows server-side; refresh the full view once, not per row.
      const bible = await brandBibleV2Service.getBible(tenantId);
      if (get().tenantId !== tenantId) {
        set({ enriching: false });
        return;
      }
      set({
        enriching: false,
        lastEnrich: enrich,
        entries: bible.tables ?? {},
        score: bible.score ?? null,
        loadedTenantId: tenantId,
      });
    } catch (err) {
      set({ enriching: false });
      throw err;
    }
  },

  runDraftJob: async <T,>(
    callerTenant: string | null | undefined,
    _kind: 'gather' | 'questionnaire',
    job: () => Promise<T>,
  ): Promise<T | undefined> => {
    const current = get().tenantId;
    if (!callerTenant) throw staleTenantError('', current ?? '');
    if (!current) return undefined;
    const tenant = callerTenant;
    if (tenant !== current) throw staleTenantError(tenant, current);
    // One live job per client, shared by the web gather and the questionnaire (the server has one ledger slot).
    if (get().gatherRunningFor === tenant) return undefined;
    set({ gatherRunningFor: tenant });
    let failed = false;
    let failure: unknown = null;
    let result: T | undefined;
    try {
      try {
        result = await job();
      } catch (err) {
        failed = true;
        failure = err;
      }
      // Always refetch (a job that errors late may still have written drafts), but only into the tenant it
      // was for.
      if (get().tenantId === tenant) {
        try {
          await refetchBible(tenant, set, get);
        } catch (err) {
          if (!failed) {
            failed = true;
            failure = err;
          }
        }
      }
    } finally {
      // Clear only OUR run: a newer run for another tenant keeps its own flag.
      if (get().gatherRunningFor === tenant) set({ gatherRunningFor: null });
    }
    // A result or a failure for a client the person has since left is dropped.
    if (get().tenantId !== tenant) return undefined;
    if (failed) throw failure;
    return result;
  },

  runGather: async (callerTenant) => {
    await get().runDraftJob(callerTenant, 'gather', async () => {
      // Inside the job: a refused (stale or empty tenant) or already-running call leaves the last result alone.
      set({ lastGather: null });
      const result = await brandBibleV2Service.gather(callerTenant);
      // The person switched client while it ran: that result is for the OLD client, drop it.
      if (get().tenantId === callerTenant) set({ lastGather: result });
      return result;
    });
  },

  acceptPendingField: async (field, valueAsSeen, value, callerTenant) => {
    const { tenantId } = get();
    if (!tenantId) return;
    if (!callerTenant || callerTenant !== tenantId) throw staleTenantError(callerTenant, tenantId);
    try {
      await brandBibleV2Service.acceptPendingField(
        tenantId,
        field,
        value === undefined ? { value_as_seen: valueAsSeen } : { value_as_seen: valueAsSeen, value },
      );
    } catch (err) {
      // stale: the draft changed under us, show what the server has now; identity_unverified
      // changed nothing, so there is nothing to refetch.
      if (apiErrorCode(err) === 'stale') await refetchBible(tenantId, set, get);
      throw err;
    }
    await refetchBible(tenantId, set, get);
  },

  rejectPendingField: async (field, valueAsSeen, callerTenant) => {
    const { tenantId } = get();
    if (!tenantId) return;
    if (!callerTenant || callerTenant !== tenantId) throw staleTenantError(callerTenant, tenantId);
    try {
      await brandBibleV2Service.rejectPendingField(tenantId, field, valueAsSeen);
    } catch (err) {
      if (apiErrorCode(err) === 'stale') await refetchBible(tenantId, set, get);
      throw err;
    }
    await refetchBible(tenantId, set, get);
  },
  };
});

/** One full-view refetch after a server-side change the store did not mirror row by row. */
async function refetchBible(
  tenantId: string,
  set: (partial: Partial<WizardState>) => void,
  get: () => WizardState,
): Promise<void> {
  const bible = await brandBibleV2Service.getBible(tenantId);
  // The store moved to another tenant while this was in flight: the result belongs to the old one.
  if (get().tenantId !== tenantId) return;
  set({ entries: bible.tables ?? {}, score: bible.score ?? null, loadedTenantId: tenantId });
}

// `gatherRunning` is derived: true only while a gather runs for the store's CURRENT tenant, so A's run
// never flags (or holds the saves of) B. A subscription keeps the stored boolean in step with
// gatherRunningFor and tenantId, whoever changed them (an init, a gather, a test setState).
useBrandBibleWizardStore.subscribe((s) => {
  const running = s.gatherRunningFor !== null && s.gatherRunningFor === s.tenantId;
  if (s.gatherRunning !== running) useBrandBibleWizardStore.setState({ gatherRunning: running });
});

/** Selectors — MUST return referentially-stable values (zustand re-renders on
 * snapshot inequality; a fresh array per call = infinite update loop). */
const EMPTY_ENTRIES: BbEntry[] = [];
const draftsCache = new WeakMap<object, BbEntry[]>();

export const selectEntries = <T extends BbTable>(table: T) =>
  (s: WizardState): BbEntry<T>[] =>
    (s.entries[table] ?? EMPTY_ENTRIES) as BbEntry<T>[];

export const selectDrafts = <T extends BbTable>(table: T) =>
  (s: WizardState): BbEntry<T>[] => {
    const list = s.entries[table] as BbEntry<T>[] | undefined;
    if (!list || list.length === 0) return EMPTY_ENTRIES as BbEntry<T>[];
    let cached = draftsCache.get(list);
    if (!cached) {
      cached = list.filter((e) => e.source === 'discovered' && e.verified_by === null);
      draftsCache.set(list, cached);
    }
    return cached as BbEntry<T>[];
  };

export const selectSessionDelta = (s: WizardState): number | null =>
  s.score !== null && s.sessionStartScore !== null ? s.score.score - s.sessionStartScore : null;
